/**
 * Bugsnag client bootstrap for Omni.
 *
 * Starts error reporting and browser performance only when a real API key is
 * present and `shouldEnableErrorReporting()` is true (not localhost /
 * development). Exports `isBugsnagInitialized` so `main.tsx` can safely create
 * the React ErrorBoundary after `start()` — calling `getPlugin('react')`
 * beforehand throws.
 */
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import BugsnagPerformance from '@bugsnag/browser-performance';
import { shouldEnableErrorReporting } from './env';

// Bugsnag SDK typings disagree with the browser ESM build; cast at the boundary.
const BugsnagClient = Bugsnag as any;

const apiKey = import.meta.env.VITE_BUGSNAG_API_KEY;
const environment = import.meta.env.VITE_ENVIRONMENT || 'development';
const appVersion = import.meta.env.VITE_APP_VERSION || '1.0.0';

let isBugsnagInitialized = false;

// Skip placeholder keys (TBD) and local/dev so we never phone home from developer machines.
if (apiKey && apiKey.trim() !== '' && apiKey !== 'TBD' && shouldEnableErrorReporting()) {
  try {
    // 1. Error client + React plugin (ErrorBoundary comes from getPlugin('react'))
    BugsnagClient.start({
      apiKey,
      plugins: [new (BugsnagPluginReact as any)()],
      releaseStage: environment,
      enabledReleaseStages: ['staging', 'production'],
      appVersion,
      sendCode: true,
      autoDetectErrors: true,
      autoTrackSessions: true,
      collectUserIp: false,
      maxBreadcrumbs: 50,
      enabledBreadcrumbTypes: ['navigation', 'request', 'process', 'log', 'user', 'state', 'error'],
    });

    // 2. Performance spans share the same project key and release-stage gate
    BugsnagPerformance.start({
      apiKey,
      releaseStage: environment,
      enabledReleaseStages: ['staging', 'production'],
    });

    isBugsnagInitialized = true;
  } catch (error) {
    console.warn('Failed to initialize Bugsnag:', error);
    isBugsnagInitialized = false;
  }
}

export default BugsnagClient;
export { isBugsnagInitialized };
